import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { getCurrentUserTypeRoles } from '../../../../util/userHelpers';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';

const SignupLink = () => {
  return (
    <NamedLink
      id="signup-link"
      name="SignupPage"
      className={classNames(css.topbarLink, css.signupButton)}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink id="login-link" name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const ApplyLink = () => {
  return (
    <NamedLink name="ApplyPage" className={classNames(css.topbarLink, css.applyButton)}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.applyAsOwner" defaultMessage="Apply as an owner" />
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({
  currentPage,
  currentUser,
  onLogout,
  showManageListingsLink,
  showProfileSettingsLink,
  isProvider,
  editListingParams,
  notificationCount,
  bookingsNotificationCount,
  messagesNotificationCount,
  inboxTab,
  intl,
}) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    const isProfileSection =
      page === 'ProfileSettingsPage' &&
      (currentPage === 'ProfileSettingsPage' || (isProvider && currentPage === 'EditListingPage'));
    return currentPage === page || isAccountSettingsPage || isProfileSection || isInboxPage
      ? css.currentPage
      : null;
  };
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  const bookingsNotificationDot =
    bookingsNotificationCount > 0 ? <div className={css.notificationDot} /> : null;
  const messagesNotificationDot =
    messagesNotificationCount > 0 ? <div className={css.notificationDot} /> : null;

  return (
    <Menu skipFocusOnNavigation={true}>
      <MenuLabel
        id="profile-menu-label"
        className={css.profileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.profileMenu' })}
      >
        <span className={css.avatarWrapper}>
          <Avatar className={css.avatar} user={currentUser} disableProfileLink />
          {notificationDot ? <span className={css.profileNotificationDot} /> : null}
        </span>
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        {isProvider ? (
          <MenuItem key="DashboardPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('DashboardPage'))}
              name="DashboardPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.dashboardLink" />
            </NamedLink>
          </MenuItem>
        ) : (
          <MenuItem key="Overview">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('DashboardPage'))}
              name="DashboardPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.overviewLink" />
            </NamedLink>
          </MenuItem>
        )}
        {isProvider ? (
          <MenuItem key="ProfileSettingsPage">
            {showProfileSettingsLink ? (
              <NamedLink
                className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
                name="ProfileSettingsPage"
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
              </NamedLink>
            ) : editListingParams ? (
              <NamedLink
                className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
                name="EditListingPage"
                params={editListingParams}
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
              </NamedLink>
            ) : (
              <NamedLink
                className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
                name="DashboardPage"
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
              </NamedLink>
            )}
          </MenuItem>
        ) : null}
        <MenuItem key="Bookings">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('BookingsPage'))}
            name="BookingsPage"
            params={{ tab: inboxTab }}
          >
            <span className={css.menuItemBorder} />
            <span className={css.menuLinkLabel}>
              <FormattedMessage id="TopbarDesktop.bookingsLink" />
              {bookingsNotificationDot}
            </span>
          </NamedLink>
        </MenuItem>
        {isProvider ? (
          <MenuItem key="Earnings">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('PaymentsPage'))}
              name="PaymentsPage"
              params={{ tab: inboxTab }}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.earningsLink" />
            </NamedLink>
          </MenuItem>
        ) : (
          <MenuItem key="Payments">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('PaymentsPage'))}
              name="PaymentsPage"
              params={{ tab: inboxTab }}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.paymentsLink" />
            </NamedLink>
          </MenuItem>
        )}
        <MenuItem key="Messages">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass(`InboxPage:${inboxTab}`))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <span className={css.menuItemBorder} />
            <span className={css.menuLinkLabel}>
              <FormattedMessage id="TopbarDesktop.messagesLink" />
              {messagesNotificationDot}
            </span>
          </NamedLink>
        </MenuItem>
        {!isProvider ? (
          <MenuItem key="ProfileSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
              name="ProfileSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    bookingsNotificationCount = 0,
    messagesNotificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
    editListingParams,
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const roles = currentUser ? getCurrentUserTypeRoles(config, currentUser) : {};
  const showProfileSettingsLink = !(currentUser && roles?.provider);
  const isProvider = !!(currentUser && roles?.provider);

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
      showProfileSettingsLink={showProfileSettingsLink}
      isProvider={isProvider}
      editListingParams={editListingParams}
      notificationCount={notificationCount}
      bookingsNotificationCount={bookingsNotificationCount}
      messagesNotificationCount={messagesNotificationCount}
      inboxTab={inboxTab}
      intl={intl}
    />
  ) : null;

  const applyLink = isAuthenticatedOrJustHydrated ? null : <ApplyLink />;
  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );

  return (
    <div className={classes}>
      <nav
        className={css.contentContainer}
        aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
      >
        <LinkedLogo
          id="logo-topbar-desktop"
          className={css.logoLink}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
        <div className={css.navRight}>
          {searchFormMaybe}
          <CustomLinksMenu
            currentPage={currentPage}
            customLinks={customLinks}
            intl={intl}
            hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
            showCreateListingsLink={showCreateListingsLink}
          />
          {applyLink}
          {profileMenuMaybe}
          {signupLinkMaybe}
          {loginLinkMaybe}
        </div>
      </nav>
    </div>
  );
};

export default TopbarDesktop;
